// Angular Imports
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

// This Module's Components
import { InputComponent } from './components/input/input.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { AutosuggestComponent } from './components/autosuggest/autosuggest.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { ButtonComponent } from './components/button/button.component';
import { BookingTypeComponent } from './components/booking-type/booking-type.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { SliderComponent } from './components/slider/slider.component';
import { ProductInfoComponent } from './components/product-info/product-info.component';
import { InformationComponent } from './components/information/information.component';
import { MessageComponent } from './components/message/message.component';
import { MaterialModule } from '../../material.module';
import { FormsModule } from '@angular/forms';
import { DatepickerComponent } from './components/datepicker/datepicker/datepicker.component';
import { LinkButtonComponent } from './components/link-button/link-button.component';
import { EqualValidator } from './directives/equal-validator.directive';
import { AgeValidator } from './directives/age-validator.directive';
import { NumberValidator } from './directives/number-validator.directive';
import { InlineSVGModule } from 'ng-inline-svg';
import { RequiredTrueValidator } from './directives/required-true-validator.directive';
import { BreadcrumbsService } from './services/breadcrumbs.service';
import { RouterModule } from '@angular/router';
import { PasswordPolicyValidator } from './directives/password-policy-validator.directive';
import { BookingTypeSimpleComponent } from './components/booking-type-simple/booking-type-simple.component';
import { BackwardForwardComponent } from './components/backward-forward/backward-forward.component';
import { CountButtonComponent } from './components/count-button/count-button.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ErrorTooltipComponent } from './components/error-tooltip/error-tooltip.component';
import { TextareaComponent } from './components/textarea/textarea.component';
import { NotificationMassageComponent } from './components/notification-massage/notification-massage.component';
import { InputNumberComponent } from './components/input-number/input-number.component';

// Booking move
import { TimeSlotsComponent } from './components/time-slots/time-slots.component';
import { TimeSlotDateSelectorComponent } from './components/time-slot-date-selector/time-slot-date-selector.component';
import { TimeSlotCalendarPopupComponent } from './components/time-slot-calendar-popup/time-slot-calendar-popup.component';
import { TimeSlotCalendarComponent } from './components/time-slot-calendar/time-slot-calendar.component';
import { TimeSlotsEventComponent } from './components/time-slots-event/time-slots-event.component';
import { ShopSettingsService } from './services/shop-settings.service';
import { ShopSettingsInMemoryService } from './services/shop-settings-in-memory.service';
import { ShopSettingsOdooService } from './services/shop-settings-odoo.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from './services/notification.service';
import { TimeLengthLabelComponent } from './components/time-length-label/time-length-label.component';
import { TouchKeyboardDirective } from './directives/touch-keyboard.directive';
import { SelectFirstInputDirective } from './directives/select-first-input.directive';
import { TextBlockComponent } from './components/text-block/text-block.component';
import { NavigationLayoutComponent } from './components/navigation-layout/navigation-layout.component';
import { H2AdaptingSizeComponent } from './components/h2-adapting-size/h2-adapting-size.component';
import { DropdownListComponent } from './components/dropdown/dropdown-list/dropdown-list.component';
import { VisualItemInfoComponent } from './components/visual-item-info/visual-item-info.component';
import { RadioComponent } from './components/radio/radio.component';
import { ModuleWithProviders } from '@angular/core';
import { TimeSlotBoxComponent } from './components/time-slot-box/time-slot-box.component';
import { TimePeriodTimeSlotComponent } from './components/time-slot-box/time-period-time-slot-box/time-period-time-slot.component';
import { DefaultTimeSlotComponent } from './components/time-slot-box/default-time-slot/default-time-slot.component';
import { OdooErrorMessagePipe } from 'src/app/pipes/odooErrorMessage';
import { HeaderTitleComponent } from './components/header-title/header-title.component';
import { HeaderTitleService } from './services/title.service';

const shopSettingsServiceClass = environment.production ? ShopSettingsOdooService : ShopSettingsInMemoryService;

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    MaterialModule,
    InlineSVGModule,
    FontAwesomeModule,
  ],
  declarations: [
    TouchKeyboardDirective,
    SelectFirstInputDirective,
    InputComponent,
    DropdownComponent,
    AutosuggestComponent,
    CheckboxComponent,
    RadioComponent,
    ButtonComponent,
    BookingTypeComponent,
    BreadcrumbsComponent,
    SliderComponent,
    ProductInfoComponent,
    InformationComponent,
    MessageComponent,
    DatepickerComponent,
    LinkButtonComponent,
    EqualValidator,
    AgeValidator,
    NumberValidator,
    RequiredTrueValidator,
    PasswordPolicyValidator,
    BookingTypeSimpleComponent,
    BackwardForwardComponent,
    CountButtonComponent,
    ErrorTooltipComponent,
    TextareaComponent,
    NotificationMassageComponent,
    TimeSlotsComponent,
    TimeSlotDateSelectorComponent,
    TimeSlotCalendarPopupComponent,
    TimeSlotCalendarComponent,
    TimeSlotsEventComponent,
    TimeLengthLabelComponent,
    TextBlockComponent,
    NavigationLayoutComponent,
    InputNumberComponent,
    H2AdaptingSizeComponent,
    DropdownListComponent,
    VisualItemInfoComponent,
    TimeSlotBoxComponent,
    TimePeriodTimeSlotComponent,
    DefaultTimeSlotComponent,
    OdooErrorMessagePipe,
    HeaderTitleComponent,
  ],
  exports: [
    BookingTypeComponent,
    ButtonComponent,
    InputComponent,
    CheckboxComponent,
    RadioComponent,
    DatepickerComponent,
    DropdownComponent,
    LinkButtonComponent,
    EqualValidator,
    AgeValidator,
    SliderComponent,
    NumberValidator,
    RequiredTrueValidator,
    BreadcrumbsComponent,
    PasswordPolicyValidator,
    BookingTypeSimpleComponent,
    BackwardForwardComponent,
    CountButtonComponent,
    ErrorTooltipComponent,
    TextareaComponent,
    NotificationMassageComponent,
    TimeSlotsComponent,
    TimeSlotDateSelectorComponent,
    TimeSlotCalendarPopupComponent,
    TimeSlotCalendarComponent,
    TimeSlotsEventComponent,
    TouchKeyboardDirective,
    SelectFirstInputDirective,
    TextBlockComponent,
    NavigationLayoutComponent,
    InputNumberComponent,
    H2AdaptingSizeComponent,
    DropdownListComponent,
    VisualItemInfoComponent,
    OdooErrorMessagePipe,
    HeaderTitleComponent,
  ],
  entryComponents: [
    MessageComponent,
    TimeSlotCalendarPopupComponent,
  ]
})
export class GeneralModule {
  static forRoot(): ModuleWithProviders<GeneralModule> {
    return {
      ngModule: GeneralModule,
      providers: [
        BreadcrumbsService,
        [{ provide: ShopSettingsService, useClass: shopSettingsServiceClass }],
        NotificationService,
        HeaderTitleService,
      ]
    };
  }
}
